import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { ApiResponse, AuthStorage } from "../types";
import { refreshTokensApi } from "../services/auth.service";

export type ApiVersion = "v1" | "v2";

export const api = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 seconds timeout
});

export let authStorage: AuthStorage | null = null;
let defaultApiVersion: ApiVersion = "v1";

// A flag to prevent multiple refresh token requests at once
let isRefreshing = false;
// A queue to hold failed requests while the token is being refreshed
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  config: InternalAxiosRequestConfig;
}> = [];
// Store the ongoing refresh promise to reuse it
let refreshPromise: Promise<any> | null = null;

// Helper function to process the queue of failed requests
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      // Update the config with the new token and resolve with the updated config
      prom.config.headers.Authorization = `Bearer ${token}`;
      prom.resolve(prom.config);
    }
  });
  failedQueue = [];
};

// Data management function for updating auth data
export const updateAuthDataWithNewTokens = async (
  currentAuthData: any,
  newAuthData: any
) => {
  // Merge the current auth data with the new auth data from refresh response
  // This ensures we update tokens and any user data that might have changed
  const updatedAuthData = {
    ...currentAuthData,
    ...newAuthData,
  };
  authStorage!.setAuthData(updatedAuthData);
  return updatedAuthData;
};

// Refactored refresh token function that uses separated concerns
export const refreshToken = async () => {
  try {
    // Get the current auth data to access the refresh token
    const authDataResult = authStorage!.getAuthData();
    const currentAuthData =
      authDataResult instanceof Promise ? await authDataResult : authDataResult;
    if (!currentAuthData?.refresh_token) {
      throw new Error("No refresh token available");
    }

    // Call the API to refresh tokens
    const refreshResponse = await refreshTokensApi(currentAuthData.refresh_token);

    // Update the stored auth data with the complete response (tokens and user data)
    await updateAuthDataWithNewTokens(currentAuthData, refreshResponse.data);

    return refreshResponse;
  } catch (error) {
    throw error;
  }
};

// --- Request Interceptor ---
// This interceptor will run before every request is sent.
// This function need to be called each time the app is started.
// It checks for a token and attaches it to the Authorization header.
/**
 * Get an API instance with a specific version
 * @param version The API version to use (defaults to configured default version)
 * @returns Axios instance with version-specific URL handling
 */
export const getVersionedApi = (version?: ApiVersion) => {
  const targetVersion = version || defaultApiVersion;
  
  return {
    ...api,
    request: async (config: InternalAxiosRequestConfig) => {
      // Add version prefix if not already present and not a full URL
      if (config.url && !config.url.startsWith("http") && !config.url.match(/^\/v[0-9]+\//)) {
        config.url = `/${targetVersion}${config.url}`;
      }
      return api.request(config);
    },
    get: async (url: string, config?: any) => {
      if (!url.startsWith("http") && !url.match(/^\/v[0-9]+\//)) {
        url = `/${targetVersion}${url}`;
      }
      return api.get(url, config);
    },
    post: async (url: string, data?: any, config?: any) => {
      if (!url.startsWith("http") && !url.match(/^\/v[0-9]+\//)) {
        url = `/${targetVersion}${url}`;
      }
      return api.post(url, data, config);
    },
    put: async (url: string, data?: any, config?: any) => {
      if (!url.startsWith("http") && !url.match(/^\/v[0-9]+\//)) {
        url = `/${targetVersion}${url}`;
      }
      return api.put(url, data, config);
    },
    delete: async (url: string, config?: any) => {
      if (!url.startsWith("http") && !url.match(/^\/v[0-9]+\//)) {
        url = `/${targetVersion}${url}`;
      }
      return api.delete(url, config);
    },
    patch: async (url: string, data?: any, config?: any) => {
      if (!url.startsWith("http") && !url.match(/^\/v[0-9]+\//)) {
        url = `/${targetVersion}${url}`;
      }
      return api.patch(url, data, config);
    }
  };
};

export const setupApi = (
  storage: AuthStorage,
  apiConfig: {
    baseURL: string;
    xClient: string;
    defaultVersion?: "v1" | "v2";
  }
) => {
  if (apiConfig) {
    if (apiConfig.baseURL) {
      api.defaults.baseURL = apiConfig.baseURL;
      if (apiConfig.baseURL.includes('ngrok')) {
        api.defaults.headers["ngrok-skip-browser-warning"] = "true";
      }
    }
    if (apiConfig.xClient) {
      api.defaults.headers["x-client-id"] = apiConfig.xClient;
    }
    if (apiConfig.defaultVersion) {
      defaultApiVersion = apiConfig.defaultVersion;
    }
  }
  if (authStorage) {
    // If the interceptor is already set up, do nothing.
    return;
  }
  authStorage = storage;

  if (!authStorage) {
    throw new Error("Auth storage not setup");
  }

  api.interceptors.request.use(
    async (config) => {
      const authDataResult = authStorage!.getAuthData();
      const authData =
        authDataResult instanceof Promise
          ? await authDataResult
          : authDataResult;

      if (authData?.access_token) {
        config.headers["Authorization"] = `Bearer ${authData.access_token}`;
      }

      // Add default version prefix if not already present and not a full URL
      if (config.url && !config.url.startsWith("http") && !config.url.match(/^\/v[0-9]+\//)) {
        config.url = `/${defaultApiVersion}${config.url}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiResponse<any>>) => {
      const originalRequest = error.config;
      if (!originalRequest) return Promise.reject(error);
      // If the error is 401 Unauthorized and it's not the refresh endpoint itself
      if (
        error.response?.status === 401 &&
        error.response?.data?.message === "Invalid or expired token" &&
        originalRequest.url !== "/auth/refresh"
      ) {
        // If a refresh is already in progress, queue the current request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject, config: originalRequest });
          })
            .then((config) => {
              return api(config as InternalAxiosRequestConfig);
            })
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        try {
          // Create or reuse the refresh promise to prevent multiple concurrent refresh calls
          if (!refreshPromise) {
            refreshPromise = refreshToken();
          }

          // Wait for the refresh to complete
          const refreshResponse = await refreshPromise;
          const { access_token: newAccessToken } = refreshResponse.data;

          // Update the header of the original failed request with the new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          // Process the queue of failed requests
          processQueue(null, newAccessToken);

          // Retry the original request
          return api(originalRequest);
        } catch (refreshError) {
          // If the refresh token request fails (e.g., refresh token is expired)
          // Clear auth data and reject all queued requests
          authStorage!.clearAuthData();
          processQueue(refreshError);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
          refreshPromise = null; // Clear the promise for future refresh attempts
        }
      }
      // For all other errors, just pass them on
      return Promise.reject(error);
    }
  );
};

/**
 * Make a request with a specific API version
 * @param version The API version to use
 * @param config Axios request configuration
 * @returns Promise with the response
 */
export const versionedRequest = async <T = any>(
  version: ApiVersion,
  config: InternalAxiosRequestConfig
): Promise<T> => {
  const apiInstance = getVersionedApi(version);
  const response = await apiInstance.request(config);
  return response.data;
};

/**
 * Create a versioned service function
 * @param version The API version to use
 * @param method HTTP method
 * @param url Endpoint URL (without version prefix)
 * @returns A function that makes the API call
 */
export const createVersionedService = <T = any, P = any>(
  version: ApiVersion,
  method: "get" | "post" | "put" | "delete" | "patch",
  url: string
) => {
  return async (params?: P): Promise<T> => {
    const apiInstance = getVersionedApi(version);

    switch (method) {
      case "get":
        return apiInstance.get(url, { params }).then((res) => res.data);
      case "post":
        return apiInstance.post(url, params).then((res) => res.data);
      case "put":
        return apiInstance.put(url, params).then((res) => res.data);
      case "delete":
        return apiInstance
          .delete(url, { data: params })
          .then((res) => res.data);
      case "patch":
        return apiInstance.patch(url, params).then((res) => res.data);
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  };
};
